import SidebarTracker from 'components/sidebar-tracker/SidebarTracker';
import SimpleBar from 'components/third-party/SimpleBar';
import { useCallback, useEffect, useRef, useState } from 'react';
import { cn } from 'utils/cn';
import Navigation from './Navigation';

// ==============================|| DRAWER CONTENT ||============================== //

const FADE_CLASS =
    'from-sidebar pointer-events-none absolute inset-x-0 h-6 to-transparent transition-opacity duration-200';

// Only the navigation scrolls; the tracker stays pinned under it, so a running timer is always in
// view however long the menu is. The navigation's top and bottom borders separate it from the
// header and the tracker, and a fade over each edge says there are rows beyond it: the top fade
// appears once the list has scrolled, the bottom one hides once the last row is in view.
const DrawerContent = () => {
    const scrollRef = useRef<HTMLElement | null>(null);
    const [edges, setEdges] = useState({ atTop: true, atBottom: true });

    const updateEdges = useCallback(() => {
        const el = scrollRef.current;
        if (!el) return;
        const atTop = el.scrollTop <= 0;
        const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 1;
        setEdges((prev) => (prev.atTop === atTop && prev.atBottom === atBottom ? prev : { atTop, atBottom }));
    }, []);

    // The list grows and shrinks as groups expand and the drawer opens, so the edges are re-read on
    // size changes, not only on scroll.
    useEffect(() => {
        updateEdges();
        const el = scrollRef.current;
        if (!el) return;
        const observer = new ResizeObserver(updateEdges);
        observer.observe(el);
        if (el.firstElementChild) observer.observe(el.firstElementChild);
        return () => observer.disconnect();
    }, [updateEdges]);

    return (
        <div className="flex min-h-0 flex-1 flex-col">
            <div className="border-sidebar-border relative min-h-0 flex-1 border-y">
                <SimpleBar className="h-full" scrollableNodeProps={{ ref: scrollRef, onScroll: updateEdges }}>
                    <Navigation />
                </SimpleBar>
                <div
                    aria-hidden
                    className={cn(FADE_CLASS, 'top-0 bg-linear-to-b', edges.atTop ? 'opacity-0' : 'opacity-100')}
                />
                <div
                    aria-hidden
                    className={cn(FADE_CLASS, 'bottom-0 bg-linear-to-t', edges.atBottom ? 'opacity-0' : 'opacity-100')}
                />
            </div>
            <SidebarTracker />
        </div>
    );
};

export default DrawerContent;
